import { useEffect, useRef } from 'react'
import classNames from 'classnames'

import { EMPTY_STRING } from '../../../constants/common'
import { useContainerWidth, useElementWidthResize } from '../../../hooks'
import {
  GRETA_AI_CHAT_SUGGESTED_PROMPTS,
  GRETA_PANEL_CHATLIST_DOCK_MIN_WIDTH,
  GRETA_PANEL_CHATLIST_MAX_WIDTH_RATIO,
  GRETA_PANEL_CHATLIST_MIN_WIDTH,
  GRETA_PANEL_COMPACT_ATTRIBUTE,
  GRETA_PANEL_STYLE_VARS,
  GRETA_PANEL_VISIBLE_ATTRIBUTE,
} from '../GretaAIChat.constants'
import { useGretaAIChatContext } from '../GretaAIChat.hooks'
import {
  getGretaAIChatVisibleSuggestedPrompts,
  getIsGretaAIChatDeleteAction,
  getIsGretaAIChatRenameAction,
  getIsGretaAIChatThinking,
} from '../GretaAIChat.utils'
import { GretaAIChatComposer } from '../GretaAIChatComposer'
import { GretaAIChatDeleteConfirmation } from '../GretaAIChatDeleteConfirmation'
import { GretaAIChatHeader } from '../GretaAIChatHeader'
import { GretaAIChatLimitNotice } from '../GretaAIChatLimitNotice'
import { GretaAIChatLockedState } from '../GretaAIChatLockedState'
import { GretaAIChatMessageList } from '../GretaAIChatMessageList'
import { GretaAIChatRenameModal } from '../GretaAIChatRenameModal'

import { GretaAIChatChatListColumn } from './GretaAIChatChatListColumn'
import { GretaAIChatChatListDrawer } from './GretaAIChatChatListDrawer'
import { useGretaAIChatPanel } from './GretaAIChatPanel.hooks'

import * as styles from './GretaAIChatPanel.module.css'

function GretaAIChatPanel() {
  const { actions, conversation, draft, panel } = useGretaAIChatContext()

  const {
    activeChatId,
    chatAction,
    chatListWidth,
    isChatListColumnOpen,
    isChatListHovered,
    isChatListOrderFrozen,
    isChatListPinned,
    isLimitReached,
    isLocked,
    isMinimized,
    mode,
    panelRef,
  } = panel

  const {
    displayName,
    feedbackByMessageId,
    isSendPending,
    messages,
    pendingAnswerFileName,
    status,
    turnStartedAt,
    onLoadOlderMessages,
    onReloadMessages,
  } = conversation

  const {
    setDraft,
    onChatListResize,
    onCloseChatAction,
    onCloseChatList,
    onConfirmDeleteChat,
    onConfirmRenameChat,
    onHoverChatList,
    onMinimize,
    onRetry,
    onSelectChat,
    onSendMessage,
    onStop,
    onSubmitFeedback,
    onToggleMode,
    onUnhoverChatList,
  } = actions

  const inputRef = useRef(null)
  const bodyRef = useRef(null)
  const chatListToggleRef = useRef(null)

  useEffect(() => {
    if (!isMinimized) {
      const input = inputRef.current

      input?.focus()
      input?.setSelectionRange(input.value.length, input.value.length)
    }
  }, [isMinimized])

  const isGenerating = getIsGretaAIChatThinking(status)

  const { handleKeyDown, handleToggleMode, isExpanded, isModeTransition } =
    useGretaAIChatPanel({
      isChatMenuOpen: isChatListOrderFrozen,
      isGenerating,
      mode,
      onMinimize,
      onStop,
      onToggleMode,
    })

  const bodyWidth = useContainerWidth(bodyRef)

  const isChatListDocked =
    isExpanded &&
    (bodyWidth === null || bodyWidth >= GRETA_PANEL_CHATLIST_DOCK_MIN_WIDTH)

  const { elementProps: chatListProps, resizeHandleProps } =
    useElementWidthResize({
      containerRef: bodyRef,
      disabled: !isChatListDocked,
      getNextWidth: ({ deltaX, startWidth }) => startWidth + deltaX,
      initialWidth: chatListWidth,
      maxWidthRatioToContainer: GRETA_PANEL_CHATLIST_MAX_WIDTH_RATIO,
      minWidth: GRETA_PANEL_CHATLIST_MIN_WIDTH,
      onResizeEnd: ({ width }) => onChatListResize(width),
    })

  const panelClassName = classNames(styles.panel, {
    [styles.compact]: !isExpanded,
    [styles.expanded]: isExpanded,
    [styles.minimized]: isMinimized,
    [styles.modeTransition]: isModeTransition,
  })

  const cornerProps = isMinimized
    ? null
    : {
        ...(isExpanded
          ? null
          : { [GRETA_PANEL_COMPACT_ATTRIBUTE]: EMPTY_STRING }),
        [GRETA_PANEL_VISIBLE_ATTRIBUTE]: EMPTY_STRING,
      }

  function handleSelectPrompt(text) {
    setDraft(text)
    inputRef.current?.focus()
  }

  function handleSelectChat(chatId) {
    if (chatId !== activeChatId) {
      onSelectChat(chatId)
    }

    onCloseChatList()
  }

  return (
    <section
      {...cornerProps}
      className={panelClassName}
      inert={isMinimized}
      ref={panelRef}
      style={GRETA_PANEL_STYLE_VARS}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <GretaAIChatHeader
        chatListToggleRef={chatListToggleRef}
        isChatListDocked={isChatListDocked}
        isExpanded={isExpanded}
        isLocked={isLocked}
        onMinimize={onMinimize}
        onToggleMode={handleToggleMode}
      />
      <div className={styles.body} ref={bodyRef}>
        {!isLocked && isChatListDocked && isChatListColumnOpen ? (
          <GretaAIChatChatListColumn
            resizeHandleProps={resizeHandleProps}
            style={chatListProps.style}
            onSelectChat={handleSelectChat}
          />
        ) : null}
        {!isLocked &&
        !isChatListDocked &&
        (isChatListPinned || isChatListHovered) ? (
          <GretaAIChatChatListDrawer
            isPinned={isChatListPinned}
            isRowMenuOpen={isChatListOrderFrozen}
            toggleRef={chatListToggleRef}
            onClose={onCloseChatList}
            onMouseEnter={onHoverChatList}
            onMouseLeave={onUnhoverChatList}
            onSelectChat={handleSelectChat}
          />
        ) : null}
        <div className={styles.main}>
          {isLocked ? (
            <GretaAIChatLockedState />
          ) : (
            <GretaAIChatMessageList
              displayName={displayName}
              feedbackByMessageId={feedbackByMessageId}
              isLimitReached={isLimitReached}
              isNewChat={activeChatId === null}
              messages={messages}
              pendingAnswerFileName={pendingAnswerFileName}
              status={status}
              suggestedPrompts={getGretaAIChatVisibleSuggestedPrompts(
                GRETA_AI_CHAT_SUGGESTED_PROMPTS
              )}
              turnStartedAt={turnStartedAt}
              onLoadOlderMessages={onLoadOlderMessages}
              onReloadMessages={onReloadMessages}
              onRetry={onRetry}
              onSelectPrompt={handleSelectPrompt}
              onSendPrompt={onSendMessage}
              onSubmitFeedback={onSubmitFeedback}
            />
          )}
          <div className={styles.strip}>
            {isLimitReached && !isLocked ? <GretaAIChatLimitNotice /> : null}

            <GretaAIChatComposer
              inputRef={inputRef}
              isGenerating={isGenerating}
              isLimitReached={isLimitReached}
              isLocked={isLocked}
              isSendPending={isSendPending}
              value={draft}
              onChange={setDraft}
              onSend={onSendMessage}
              onStop={onStop}
            />
          </div>
        </div>
      </div>
      {getIsGretaAIChatRenameAction(chatAction) ? (
        <GretaAIChatRenameModal
          chat={chatAction.chat}
          onCancel={onCloseChatAction}
          onSubmit={onConfirmRenameChat}
        />
      ) : null}
      {getIsGretaAIChatDeleteAction(chatAction) ? (
        <GretaAIChatDeleteConfirmation
          onCancel={onCloseChatAction}
          onConfirm={onConfirmDeleteChat}
        />
      ) : null}
    </section>
  )
}

export default GretaAIChatPanel
